import type { MapBook } from './availability'
import { trackMapAffiliateClick } from '../lib/analytics'

// Sportsbook brand tile — brand-color square with the logo mark, wrapped in the
// affiliate link when the sport has one for that book. Shared by the map hover
// popovers and the list view; the list uses size="sm" so more books fit per row.

interface Props {
  book: MapBook
  apiBase: string
  sport: string
  /** USPS state code or ISO alpha-2 country code — reported to GA4. */
  place: string
  scope: 'state' | 'country'
  size?: 'md' | 'sm'
  /** Sportsbook mode: the tile is a LEAGUE logo — no brand background, no link. */
  plain?: boolean
}

export default function BookTile({ book, apiBase, sport, place, scope, size = 'md', plain = false }: Props) {
  const src = book.logo_url
    ? (book.logo_url.startsWith('http') ? book.logo_url : apiBase + book.logo_url)
    : null

  // Sub-national coverage limits ride along on the tooltip so the limit is
  // attached to the book even where the surrounding text has no room for it.
  const qualifier = scope === 'country' ? book.country_notes?.[place] : null
  const title = qualifier ? `${book.name} — ${qualifier}` : book.name

  const tile = (
    <span
      className={`am-tile${size === 'sm' ? ' am-tile-sm' : ''}${book.logo_fill ? ' am-tile-fill' : ''}${plain ? ' is-plain' : ''}${plain && book.logo_dark ? ' is-dark' : ''}`}
      style={plain ? undefined : { background: book.brand_color ?? '#e7e8ef' }}
      title={title}
      data-sport={plain ? book.slug ?? undefined : undefined}
    >
      {src
        ? <img src={src} alt={book.name} loading="lazy" />
        : <span className="am-tile-txt">{book.name.slice(0, 2).toUpperCase()}</span>}
    </span>
  )

  // League logos (sportsbook mode) are never links — Cole 2026-10-07.
  if (plain || !book.affiliate_url) return tile

  return (
    <a
      className="am-tile-link"
      href={book.affiliate_url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={book.name}
      onClick={() => trackMapAffiliateClick(sport, book.name, book.kind, place, scope)}
    >
      {tile}
    </a>
  )
}
